import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import {
  XMarkIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  TruckIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { vehicleMappingService } from '../../services/logistics/vehicle-mapping';
import { vehiclesService } from '../../services/logistics/vehicles';
import { shipmentsService } from '../../services/logistics/shipments';
import LoadingSpinner from '../ui/LoadingSpinner';
import toast from 'react-hot-toast';

const VehicleMappingManagementModal = ({ isOpen, onClose, onMappingsUpdated }) => {
  const { t } = useTranslation();
  const { currentCompany, user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [mappings, setMappings] = useState({});
  const [vehicles, setVehicles] = useState([]);
  const [shipmentPlates, setShipmentPlates] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingMapping, setEditingMapping] = useState(null);
  const [newMapping, setNewMapping] = useState({ petroApp: '', system: '' });
  const [showAddForm, setShowAddForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);

  useEffect(() => {
    if (isOpen && currentCompany?.id) {
      loadData();
    }
  }, [isOpen, currentCompany?.id]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      const [mappingsData, vehiclesData, shipmentsData] = await Promise.all([
        vehicleMappingService.getVehicleMappings(currentCompany.id),
        vehiclesService.getVehicles(currentCompany.id),
        shipmentsService.getShipments(currentCompany.id)
      ]);

      setMappings(mappingsData);
      setVehicles(vehiclesData);
      
      // Extract unique vehicle plates from shipments
      const uniquePlates = [...new Set(
        shipmentsData
          .filter(s => s.vehiclePlate && s.vehiclePlate !== 'Unknown Vehicle')
          .map(s => s.vehiclePlate)
      )];
      setShipmentPlates(uniquePlates);
      
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error(t('fuel.failed_to_load_vehicle_mappings'));
    } finally {
      setLoading(false);
    }
  };

  const handleSaveMapping = async (petroApp, systemPlate) => {
    if (!petroApp || !systemPlate) {
      toast.error(t('fuel.both_plates_required'));
      return;
    }

    try {
      setSaving(true);
      await vehicleMappingService.saveVehicleMapping(
        currentCompany.id,
        petroApp,
        systemPlate,
        user.uid
      );

      setMappings(prev => ({
        ...prev,
        [petroApp]: systemPlate
      }));

      toast.success(t('fuel.vehicle_mapping_saved_successfully'));
      setEditingMapping(null);
      setShowAddForm(false);
      setNewMapping({ petroApp: '', system: '' });

      if (onMappingsUpdated) {
        onMappingsUpdated();
      }
    } catch (error) {
      console.error('Error saving mapping:', error);
      toast.error(t('fuel.failed_to_save_vehicle_mapping'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteMapping = async (petroApp) => {
    try {
      await vehicleMappingService.deleteVehicleMapping(currentCompany.id, petroApp);
      
      const updatedMappings = { ...mappings };
      delete updatedMappings[petroApp];
      setMappings(updatedMappings);
      
      toast.success(t('fuel.vehicle_mapping_deleted_successfully'));
      setDeleteConfirmation(null);
      
      if (onMappingsUpdated) {
        onMappingsUpdated();
      }
    } catch (error) {
      console.error('Error deleting mapping:', error);
      toast.error(t('fuel.failed_to_delete_vehicle_mapping'));
    }
  };

  const getAllSystemPlates = () => {
    const vehiclePlates = vehicles.map(v => v.plateNumber).filter(Boolean);
    return [...new Set([...vehiclePlates, ...shipmentPlates])].sort();
  };

  const filteredMappings = Object.entries(mappings).filter(([petroApp, systemPlate]) => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return petroApp.toLowerCase().includes(search) || 
           systemPlate.toLowerCase().includes(search);
  });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center space-x-3">
            <TruckIcon className="h-6 w-6 text-blue-600" />
            <h2 className="text-xl font-semibold text-gray-900">
              {t('fuel.vehicle_mapping_management')}
            </h2>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={loadData}
              disabled={loading}
              className="p-2 text-gray-400 hover:text-gray-600"
              title="Refresh"
            >
              <ArrowPathIcon className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {loading ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner size="lg" />
            </div>
          ) : (
            <div className="space-y-6">
              {/* Controls */}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="relative">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder={t('fuel.search_mappings')}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div className="text-sm text-gray-600">
                    {Object.keys(mappings).length} {t('fuel.mappings_total')}
                  </div>
                </div>
                
                <button
                  onClick={() => setShowAddForm(true)}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  <PlusIcon className="h-4 w-4" />
                  <span>{t('fuel.add_mapping')}</span>
                </button>
              </div>

              {/* Add New Mapping Form */}
              {showAddForm && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h3 className="text-lg font-medium text-blue-900 mb-4">{t('fuel.add_new_vehicle_mapping')}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('fuel.petroapp_vehicle_plate')}
                      </label>
                      <input
                        type="text"
                        value={newMapping.petroApp}
                        onChange={(e) => setNewMapping(prev => ({ ...prev, petroApp: e.target.value }))}
                        placeholder={t('fuel.enter_petroapp_vehicle_plate')}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('fuel.system_vehicle_plate')}
                      </label>
                      <select
                        value={newMapping.system}
                        onChange={(e) => setNewMapping(prev => ({ ...prev, system: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">{t('fuel.select_system_vehicle')}</option>
                        {getAllSystemPlates().map(plate => (
                          <option key={plate} value={plate}>{plate}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3 mt-4">
                    <button
                      onClick={() => handleSaveMapping(newMapping.petroApp, newMapping.system)}
                      disabled={!newMapping.petroApp || !newMapping.system || saving}
                      className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                    >
                      {saving && <LoadingSpinner size="sm" />}
                      <CheckCircleIcon className="h-4 w-4" />
                      <span>{t('fuel.save_mapping')}</span>
                    </button>
                    <button
                      onClick={() => {
                        setShowAddForm(false);
                        setNewMapping({ petroApp: '', system: '' });
                      }}
                      className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                      {t('fuel.cancel')}
                    </button>
                  </div>
                </div>
              )}

              {/* Mappings Table */}
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('fuel.petroapp_vehicle')}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('fuel.system_vehicle')}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('fuel.status')}
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('fuel.actions')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredMappings.length === 0 ? (
                        <tr>
                          <td colSpan="4" className="px-6 py-8 text-center text-gray-500">
                            {searchTerm ? t('fuel.no_mappings_match_search') : t('fuel.no_vehicle_mappings_found')}
                          </td>
                        </tr>
                      ) : (
                        filteredMappings.map(([petroApp, systemPlate]) => (
                          <tr key={petroApp} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              {editingMapping === petroApp ? (
                                <input
                                  type="text"
                                  value={petroApp}
                                  disabled
                                  className="w-full px-3 py-1 text-sm border border-gray-300 rounded bg-gray-100"
                                />
                              ) : (
                                <div className="flex items-center space-x-2">
                                  <TruckIcon className="h-4 w-4 text-blue-500" />
                                  <span className="text-sm font-medium text-gray-900">{petroApp}</span>
                                </div>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {editingMapping === petroApp ? (
                                <select
                                  value={systemPlate}
                                  onChange={(e) => {
                                    const newSystemPlate = e.target.value;
                                    setMappings(prev => ({
                                      ...prev,
                                      [petroApp]: newSystemPlate
                                    }));
                                  }}
                                  className="w-full px-3 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                                >
                                  {getAllSystemPlates().map(plate => (
                                    <option key={plate} value={plate}>{plate}</option>
                                  ))}
                                </select>
                              ) : (
                                <div className="flex items-center space-x-2">
                                  <TruckIcon className="h-4 w-4 text-green-500" />
                                  <span className="text-sm text-gray-900">{systemPlate}</span>
                                </div>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center space-x-1">
                                <CheckCircleIcon className="h-4 w-4 text-green-500" />
                                <span className="text-xs text-green-700 bg-green-100 px-2 py-1 rounded-full">
                                  {t('fuel.active')}
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              {editingMapping === petroApp ? (
                                <div className="flex items-center justify-end space-x-2">
                                  <button
                                    onClick={() => {
                                      handleSaveMapping(petroApp, mappings[petroApp]);
                                    }}
                                    disabled={saving}
                                    className="text-green-600 hover:text-green-900"
                                  >
                                    <CheckCircleIcon className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => {
                                      setEditingMapping(null);
                                      loadData(); // Reload to reset changes
                                    }}
                                    className="text-gray-600 hover:text-gray-900"
                                  >
                                    <XMarkIcon className="h-4 w-4" />
                                  </button>
                                </div>
                              ) : (
                                <div className="flex items-center justify-end space-x-2">
                                  <button
                                    onClick={() => setEditingMapping(petroApp)}
                                    className="text-blue-600 hover:text-blue-900"
                                  >
                                    <PencilIcon className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => setDeleteConfirmation(petroApp)}
                                    className="text-red-600 hover:text-red-900"
                                  >
                                    <TrashIcon className="h-4 w-4" />
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Statistics */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="text-2xl font-bold text-blue-600">
                    {Object.keys(mappings).length}
                  </div>
                  <div className="text-sm text-blue-800">
                    {t('fuel.total_mappings')}
                  </div>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                  <div className="text-2xl font-bold text-green-600">
                    {vehicles.length}
                  </div>
                  <div className="text-sm text-green-800">
                    {t('fuel.system_vehicles')}
                  </div>
                </div>
                <div className="bg-purple-50 rounded-lg p-4">
                  <div className="text-2xl font-bold text-purple-600">
                    {shipmentPlates.length}
                  </div>
                  <div className="text-sm text-purple-800">
                    {t('fuel.shipment_plates')}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 bg-gray-50 border-t">
          <div className="text-sm text-gray-600">
            {t('fuel.manage_mappings_description')}
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            {t('fuel.close')}
          </button>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      {deleteConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
                <h3 className="text-lg font-medium text-gray-900">
                  {t('fuel.delete_vehicle_mapping')}
                </h3>
              </div>
              <p className="text-gray-600 mb-6">
                {t('fuel.delete_mapping_confirmation', { vehiclePlate: deleteConfirmation })}
              </p>
              <div className="flex items-center justify-end space-x-3">
                <button
                  onClick={() => setDeleteConfirmation(null)}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  {t('fuel.cancel')}
                </button>
                <button
                  onClick={() => handleDeleteMapping(deleteConfirmation)}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                >
                  {t('fuel.delete')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VehicleMappingManagementModal;